import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Shield, MapPin, X, ExternalLink } from "lucide-react";

const pdpaContent = `เว็บไซต์ **Postperty.com** โดยครอบคลุมประเด็นสำคัญต่าง ๆ เช่น นโยบายความเป็นส่วนตัว (Privacy Policy) การขอความยินยอม (Consent) และมาตรการรักษาความปลอดภัยของข้อมูล (Data Security)

### 1. **นโยบายความเป็นส่วนตัว (Privacy Policy)**
**Postperty.com** มุ่งมั่นในการคุ้มครองข้อมูลส่วนบุคคลของผู้ใช้งานตามกฎหมาย PDPA (พระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562) นโยบายนี้อธิบายถึงวิธีการเก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคลของคุณ

#### ข้อมูลที่เรารวบรวม
- ข้อมูลส่วนบุคคล เช่น ชื่อ นามสกุล ที่อยู่ เบอร์โทรศัพท์ อีเมล
- ข้อมูลบัญชีผู้ใช้งาน เช่น ชื่อผู้ใช้งาน (Username) และรหัสผ่าน (Password)
- ข้อมูลการใช้งานเว็บไซต์ เช่น ประวัติการเข้าชม (Log Data) และคุกกี้ (Cookies)
- ข้อมูล ตำแหน่ง พิกัด GPS ที่ได้ จากอุปกรณ์ สื่อสาร ในขณะที่ลูกค้ากำลังใช้งาน เวปไซต์

#### วัตถุประสงค์ของการใช้ข้อมูล
- เพื่อการลงทะเบียนและให้บริการบนเว็บไซต์
- เพื่อการติดต่อสื่อสารและให้ข้อมูลเกี่ยวกับบริการ
- เพื่อการปรับปรุงและพัฒนาบริการของเรา
- เพื่อการตลาดและเสนอโปรโมชันต่าง ๆ (หากได้รับความยินยอมจากคุณ)

#### การเปิดเผยข้อมูล
เราจะไม่เปิดเผยข้อมูลส่วนบุคคลของคุณให้กับบุคคลที่สามโดยไม่ได้รับความยินยอมจากคุณ ยกเว้นในกรณีที่กฎหมายกำหนด

#### สิทธิ์ของคุณ
คุณมีสิทธิ์ดังต่อไปนี้:
- ขอเข้าถึงข้อมูลส่วนบุคคลของคุณ
- ขอแก้ไขข้อมูลส่วนบุคคลของคุณ
- ขอลบข้อมูลส่วนบุคคลของคุณ
- ถอนความยินยอมในการใช้ข้อมูลส่วนบุคคลของคุณ

#### การติดต่อ
หากมีคำถามหรือต้องการใช้สิทธิ์ตามกฎหมาย PDPA กรุณาติดต่อเราที่:
- อีเมล: chanwit@postperty.com
- เบอร์โทรศัพท์: 091-0920333

---

### 2. **การขอความยินยอม (Consent)**
เมื่อผู้ใช้งานลงทะเบียนหรือใช้บริการบนเว็บไซต์ **Postperty.com** จะมีการแสดงข้อความขอความยินยอมดังนี้:

> "เพื่อให้การให้บริการเป็นไปอย่างมีประสิทธิภาพ ทางเว็บไซต์ Postperty.com จำเป็นต้องเก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคลของคุณตามนโยบายความเป็นส่วนตัว คุณสามารถอ่านรายละเอียดเพิ่มเติมได้ที่ [ลิงก์นโยบายความเป็นส่วนตัว] หากคุณยินยอม กรุณากดปุ่ม 'ยอมรับ' หรือดำเนินการต่อไปเพื่อใช้บริการ"

**หมายเหตุ**:
- ผู้ใช้งานสามารถถอนความยินยอมได้ตลอดเวลาโดยติดต่อเราผ่านช่องทางที่ระบุในนโยบายความเป็นส่วนตัว

### 3. **มาตรการรักษาความปลอดภัยของข้อมูล (Data Security)**
**Postperty.com** มีมาตรการรักษาความปลอดภัยของข้อมูลส่วนบุคคลดังนี้:
1. **การเข้ารหัสข้อมูล (Encryption)**: ข้อมูลส่วนบุคคลทั้งหมดจะถูกเข้ารหัสทั้งในระหว่างการส่งและจัดเก็บ
2. **การควบคุมการเข้าถึง (Access Control)**: จำกัดการเข้าถึงข้อมูลส่วนบุคคลเฉพาะผู้ที่มีความจำเป็นต้องรู้เท่านั้น
3. **การตรวจสอบระบบ (Audit)**: มีการตรวจสอบระบบอย่างสม่ำเสมอเพื่อป้องกันการเข้าถึงข้อมูลโดยไม่ได้รับอนุญาต
4. **การฝึกอบรมพนักงาน**: พนักงานทุกคนจะได้รับการฝึกอบรมเกี่ยวกับการคุ้มครองข้อมูลส่วนบุคคลตามกฎหมาย PDPA

---

### 4. **แผนรับมือกรณีข้อมูลรั่วไหล (Data Breach Response Plan)**
หากเกิดกรณีข้อมูลรั่วไหล **Postperty.com** จะดำเนินการดังนี้:
1. **ตรวจสอบและประเมินสถานการณ์**: ทีมงานจะตรวจสอบสาเหตุและขอบเขตของเหตุการณ์
2. **แจ้งสำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (สคส.)**: จะแจ้งเหตุการณ์ภายใน 72 ชั่วโมงหลังจากทราบเหตุ
3. **แจ้งผู้ใช้งานที่ได้รับผลกระทบ**: หากมีความเสี่ยงสูงต่อสิทธิเสรีภาพของผู้ใช้งาน จะแจ้งให้ผู้ใช้งานทราบพร้อมคำแนะนำในการป้องกันตนเอง
4. **แก้ไขและป้องกันการเกิดซ้ำ**: จะดำเนินการแก้ไขจุดบกพร่องและปรับปรุงระบบเพื่อป้องกันการเกิดซ้ำ

---

### 5. **การแต่งตั้ง DPO (เจ้าหน้าที่คุ้มครองข้อมูลส่วนบุคคล)**
**Postperty.com** จะแต่งตั้ง DPO เพื่อดูแลการปฏิบัติตามกฎหมาย PDPA โดย DPO จะมีหน้าที่ดังนี้:
- ให้คำแนะนำเกี่ยวกับการคุ้มครองข้อมูลส่วนบุคคล
- ตรวจสอบการปฏิบัติตามกฎหมาย PDPA
- เป็นผู้ประสานงานกับสำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (สคส.)
`;

const Settings = () => {
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [isPDPAModalOpen, setIsPDPAModalOpen] = useState(false);

  const handleAllowLocationAccess = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationAllowed(true);
          Swal.fire("สำเร็จ!", "การเข้าถึงตำแหน่งที่ตั้งได้รับอนุญาต", "success");
          console.log("Position:", position);
        },
        (error) => {
          let errorMessage = "";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "การเข้าถึงตำแหน่งที่ตั้งถูกปฏิเสธโดยผู้ใช้ กรุณาดูยูทูปสอนเพื่อวิธีแก้ไขอนุญาตการเข้าถึงตำแหน่งของท่าน";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "ไม่สามารถระบุตำแหน่งได้";
              break;
            case error.TIMEOUT:
              errorMessage = "การขอข้อมูลตำแหน่งเกินระยะเวลาที่กำหนด";
              break;
            default:
              errorMessage = "เกิดข้อผิดพลาดในการขอตำแหน่งที่ตั้ง";
              break;
          }
          Swal.fire("เกิดข้อผิดพลาด!", errorMessage, "error");
        }
      );
    } else {
      Swal.fire("ไม่รองรับ", "เบราว์เซอร์ของคุณไม่รองรับการเข้าถึงตำแหน่งที่ตั้ง", "error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 lg:pt-20">
      <div className="max-w-xl mx-auto px-4 py-8 space-y-4">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 flex items-center gap-3"
        >
          <Link to="/profiles">
            <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 hover:bg-gray-100 transition-colors flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          </Link>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">โปรไฟล์</p>
            <h1 className="text-lg font-bold text-gray-900">การตั้งค่า</h1>
          </div>
        </motion.div>

        {/* Menu items */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.04 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-50"
        >
          {/* PDPA */}
          <button
            onClick={() => setIsPDPAModalOpen(true)}
            className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
          >
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">นโยบายความเป็นส่วนตัว (PDPA)</p>
              <p className="text-xs text-gray-400 mt-0.5">อ่านนโยบายการคุ้มครองข้อมูลส่วนบุคคล</p>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Location permission */}
          <button
            onClick={handleAllowLocationAccess}
            className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${locationAllowed ? "bg-emerald-50" : "bg-amber-50"}`}>
              <MapPin className={`w-5 h-5 ${locationAllowed ? "text-emerald-600" : "text-amber-500"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">อนุญาตการเข้าถึงตำแหน่งที่ตั้ง</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {locationAllowed ? "ได้รับอนุญาตแล้ว" : "กดเพื่ออนุญาตให้เข้าถึง GPS"}
              </p>
            </div>
            {locationAllowed ? (
              <span className="text-xs px-2.5 py-1 bg-emerald-50 text-emerald-600 font-semibold rounded-full border border-emerald-100 flex-shrink-0">เปิดแล้ว</span>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>

          {/* How-to video link */}
          <Link
            to="https://youtu.be/Yv21fdWMr-g"
            target="_blank"
            className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">วิธีการอนุญาตตำแหน่ง</p>
              <p className="text-xs text-gray-400 mt-0.5">ดูวิดีโอสอนการตั้งค่าบน YouTube</p>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-300 flex-shrink-0" />
          </Link>
        </motion.div>

        <div className="h-8" />
      </div>

      {/* PDPA Modal */}
      <AnimatePresence>
        {isPDPAModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsPDPAModalOpen(false)}
          >
            <motion.div
              className="bg-white w-full sm:w-11/12 sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl p-6 relative shadow-xl"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 60 }}
              transition={{ duration: 0.25 }}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Shield className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">นโยบายความเป็นส่วนตัว (PDPA)</h2>
                </div>
                <button
                  onClick={() => setIsPDPAModalOpen(false)}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="prose prose-sm max-w-full prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600">
                <ReactMarkdown>{pdpaContent}</ReactMarkdown>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Settings;
